import { motion } from 'motion/react';
import { useCallback, useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { create } from 'zustand';
import { OrgusMark } from '@orgus/ui';
import { startData } from '../../lib/data.ts';
import { useOrgus } from '../../lib/store.ts';
import { SignInScreen } from './SignInScreen.tsx';
import type { SignedOutReason } from './signinStates.ts';
import { openedWithOwnerLink, takeOwnerLinkFromHash, useMe } from './useMe.ts';
import type { MeEndedBy, MeStatus } from './useMe.ts';

/** After this long the splash says what it is waiting for. */
export const SPLASH_SLOW_MS = 4000;

/**
 * While the hub is asked who this browser is: the mark on the page colour, nothing else, so
 * neither the app nor the sign-in screen flashes in first. It fades in late (most answers come
 * before it shows) and, if the hub is slow, says so.
 */
export function AuthSplash({ host = typeof window !== 'undefined' ? window.location.host : '' }: { host?: string }) {
  const [slow, setSlow] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setSlow(true), SPLASH_SLOW_MS);
    return () => clearTimeout(t);
  }, []);
  return (
    <div role="status" aria-busy="true" data-auth="checking" className="flex h-full min-h-full w-full flex-col items-center justify-center gap-4 bg-bg px-6 text-center text-fg">
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.25, duration: 0.4 }} className="flex flex-col items-center gap-4">
        <OrgusMark size={32} decorative />
        <p className="text-sm text-fg-subtle">Checking your session</p>
      </motion.div>
      {slow && (
        <p className="max-w-xs text-sm text-fg-muted">
          Still waiting for the hub at <span className="font-mono">{host}</span>. It may be starting up; this page continues by itself when it answers.
        </p>
      )}
    </div>
  );
}

/** What the gate renders for a status. Pure, so it is tested without a browser. */
export function gateView(status: MeStatus, signedOutLast: boolean): 'splash' | 'sign-in' | 'app' {
  if (status === 'loading') return 'splash';
  if (status === 'signed-out') return 'sign-in';
  // The hub stopped answering while the sign-in screen was up: stay there and say so.
  if (status === 'error' && signedOutLast) return 'sign-in';
  return 'app';
}

export type GateView = ReturnType<typeof gateView>;

/**
 * What the gate shows right now, for parts of the page that live outside it: the phone-layout
 * offer is mounted beside the shell and reads this to stay off the splash and the sign-in screen.
 */
export const useGateView = create<{ view: GateView }>()(() => ({ view: 'splash' }));

/**
 * Shows the sign-in screen instead of the app when the hub says this browser is signed out,
 * and a calm splash until it has answered. A hub without the auth module, or one that cannot
 * be reached on first load, falls through to the app, whose pages show their own states.
 */
/** The sign-in screen's reason for each way the hub ends a stream (the user menu's own sign-out shows none). */
const STREAM_END_REASON: Record<MeEndedBy, SignedOutReason> = {
  'signed-out': 'signed-out-elsewhere',
  removed: 'removed',
  revoked: 'revoked',
  expired: 'expired',
};

export function AuthGate({ children }: { children: ReactNode }) {
  const status = useMe((s) => s.status);
  const error = useMe((s) => s.error);
  const mode = useOrgus((s) => s.mode);
  const phase = useOrgus((s) => s.phase);
  const loadError = useOrgus((s) => s.loadError);
  const [signedOutLast, setSignedOutLast] = useState(() => useMe.getState().status === 'signed-out');
  const [reason, setReason] = useState<SignedOutReason | null>(null);
  const rechecking = useRef(0);

  /** Ask the hub again; a signed-in browser that comes back signed out had its session end. */
  const recheck = useCallback(async () => {
    rechecking.current++;
    try {
      await useMe.getState().load();
    } finally {
      rechecking.current--;
    }
  }, []);

  // Why the sign-in screen is up: the session ended under us, or someone signed out (the user
  // menu calls setSignedOut directly, never through recheck).
  useEffect(
    () =>
      useMe.subscribe((s, prev) => {
        if (s.status === prev.status) return;
        if (s.status === 'signed-out') {
          setSignedOutLast(true);
          // The hub ended the live stream and said why (SD-2); else a re-check found the session gone.
          if (prev.status === 'signed-in') setReason(s.endedBy ? STREAM_END_REASON[s.endedBy] : rechecking.current > 0 ? 'session-ended' : 'signed-out');
        } else if (s.status === 'signed-in' || s.status === 'unsupported') {
          setSignedOutLast(false);
          setReason(null);
        }
      }),
    [],
  );

  // Ask the hub who we are. When the page was opened with an owner link, wait until the
  // data layer has exchanged it for a cookie (the first load settles), or we would see 401.
  useEffect(() => {
    if (openedWithOwnerLink() && mode === 'live' && phase === 'loading') return;
    void useMe.getState().load();
  }, [mode, phase === 'loading']);

  // A sign-in link pasted into the address bar of this tab changes only the hash, so the page
  // does not reload: exchange it now, the same way as at load.
  useEffect(() => {
    const onHash = () => void takeOwnerLinkFromHash(window.location.hash, () => startData());
    window.addEventListener('hashchange', onHash);
    return () => window.removeEventListener('hashchange', onHash);
  }, []);

  // A 401 from the data layer while we think we are signed in: the session ended.
  useEffect(() => {
    if (loadError?.status === 401 && useMe.getState().status === 'signed-in') void recheck();
  }, [loadError, recheck]);

  // Coming back to the tab, or back online, is a good moment to notice a change.
  useEffect(() => {
    const again = () => {
      if (useMe.getState().status !== 'loading') void recheck();
    };
    window.addEventListener('focus', again);
    window.addEventListener('online', again);
    return () => {
      window.removeEventListener('focus', again);
      window.removeEventListener('online', again);
    };
  }, [recheck]);

  const view = gateView(status, signedOutLast);
  useEffect(() => {
    useGateView.setState({ view });
  }, [view]);
  if (view === 'splash') return <AuthSplash />;
  if (view === 'sign-in') return <SignInScreen reason={reason} hubError={status === 'error' ? error : null} onRetry={recheck} />;
  return <>{children}</>;
}
